import { useState, useCallback } from 'react';

export interface UseDeleteWithConfirmationOptions {
  onDelete: (id: string) => Promise<any>;
  onSuccess?: (id: string) => void | Promise<void>;
  onError?: (error: any, id: string) => void;
  confirmationMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

export interface UseDeleteWithConfirmationReturn {
  isDeleting: boolean;
  deleteItem: (id: string, customMessage?: string) => Promise<void>;
}

/**
 * Hook for deleting items with confirmation dialog
 * Handles confirmation, loading state, and error handling
 * 
 * @param options - Configuration for delete operation
 * @returns Delete function and loading state
 * 
 * @example
 * const { isDeleting, deleteItem } = useDeleteWithConfirmation({
 *   onDelete: async (id) => await deleteUser(id).unwrap(),
 *   onSuccess: async (id) => await refetch(),
 *   confirmationMessage: 'Are you sure you want to delete this user?'
 * });
 * 
 * <Button onClick={() => deleteItem(userId)} disabled={isDeleting}>
 *   Delete
 * </Button>
 */
export const useDeleteWithConfirmation = ({
  onDelete,
  onSuccess,
  onError,
  confirmationMessage = 'Are you sure you want to delete this item? This action cannot be undone.',
  successMessage,
  errorMessage = 'Failed to delete item',
}: UseDeleteWithConfirmationOptions): UseDeleteWithConfirmationReturn => {
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteItem = useCallback(async (id: string, customMessage?: string) => {
    const message = customMessage || confirmationMessage;
    
    if (!window.confirm(message)) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(id);
      
      // Call onSuccess if provided
      if (onSuccess) {
        await onSuccess(id);
      }
      
      // Show success message if provided
      if (successMessage) {
        // Could use a toast/notification system here
        console.log(successMessage);
      }
    } catch (error) {
      const finalErrorMessage = errorMessage || 'Error deleting item';
      console.error(finalErrorMessage, error);
      
      // Call onError if provided
      if (onError) {
        onError(error, id);
      } else {
        // Default: show alert
        alert(finalErrorMessage);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete, onSuccess, onError, confirmationMessage, successMessage, errorMessage]);

  return {
    isDeleting,
    deleteItem,
  };
};
